import React, { useState } from 'react';
import moment, { Moment } from 'moment';
import { Form, Typography, DatePicker, Button, Checkbox } from 'antd';
// Hooks
import { useCampaignManagement } from 'hooks/useCampaignManagement';
// Constants
import { onlyDateTimeFormat } from 'constants/global';
// Types
import { GetCampaignsV2_getCampaigns_entities } from 'api/campaignV2/types/GetCampaignsV2';
import { RangePickerProps } from 'antd/lib/date-picker';
// Ui
import { errorNotification, successNotification } from 'ui/Notification';
// Styles
import styles from './EditDeadlineModal.module.scss';

const { Text } = Typography;

type EditDeadlineModalProps = {
  record: GetCampaignsV2_getCampaigns_entities;
  onFinish: () => void;
};

export type EditDeadlineFormValues = {
  applicationDeadlineEnabled: boolean;
  dueDate: Moment | null;
};

const EditDeadlineModal = ({
  record,
  onFinish,
}: EditDeadlineModalProps): JSX.Element | null => {
  const [applicationDeadlineEnabled, setApplicationDeadlineEnabled] =
    useState<boolean>(record?.applicationDeadlineEnabled ?? true);

  const [form] = Form.useForm();

  const { updateCampaignDeadlines, loadingUpdateCampaignDeadlines } =
    useCampaignManagement();

  const initialFormValues: EditDeadlineFormValues = {
    applicationDeadlineEnabled: record?.applicationDeadlineEnabled ?? true,
    dueDate: record?.dueDate ? moment(record.dueDate) : null,
  };

  const disabledDate: RangePickerProps['disabledDate'] = (current) => {
    // allow days starting with today
    return current && current < moment().startOf('day');
  };

  const handleApplicationDeadlineEnabledChange = (checked: boolean) => {
    setApplicationDeadlineEnabled(checked);
  };

  const handleSubmit = async () => {
    try {
      const { applicationDeadlineEnabled, dueDate } = form.getFieldsValue();

      const updateCampaignInputVariables = {
        campaignId: record.id,
        applicationDeadlineEnabled,
        ...(applicationDeadlineEnabled && dueDate && { dueDate }),
      };

      const { data } = await updateCampaignDeadlines({
        variables: {
          input: updateCampaignInputVariables,
        },
      });

      if (data?.updateCampaignDeadlines) {
        successNotification('The campaign deadlines were successfully updated');
        onFinish();
      }
    } catch (error) {
      errorNotification((error as Error)?.message);
      console.error('Campaign update deadlines error:', { error });
    }
  };

  const submitButtonDisabled =
    !form.isFieldsValidating || loadingUpdateCampaignDeadlines;

  return (
    <div className={styles.root}>
      <Form
        name="EditDeadlineForm"
        aria-label="Update Deadline"
        onFinish={handleSubmit}
        form={form}
        initialValues={initialFormValues}
      >
        <div className={styles.columnWrapper}>
          {record?.applicationDeadlineDays && (
            <Form.Item
              name="applicationDeadlineEnabled"
              label={<Text>Deadline Enabled</Text>}
              valuePropName="checked"
            >
              <Checkbox
                checked={applicationDeadlineEnabled}
                onChange={(e) =>
                  handleApplicationDeadlineEnabledChange(e.target.checked)
                }
              />
            </Form.Item>
          )}

          <div className={styles.deadlineWrapper}>
            <Form.Item
              name="dueDate"
              rules={[
                {
                  required: applicationDeadlineEnabled,
                  message: 'This field is required',
                },
              ]}
              label={<Text>Application deadline date</Text>}
              style={{ marginBottom: '4px' }}
            >
              <DatePicker
                name="dueDate"
                format={onlyDateTimeFormat}
                disabledDate={disabledDate}
                disabled={!applicationDeadlineEnabled}
              />
            </Form.Item>
          </div>
        </div>

        <div className={styles.submitButtonWrapper}>
          <Button
            type="primary"
            htmlType="submit"
            loading={loadingUpdateCampaignDeadlines}
            disabled={submitButtonDisabled}
          >
            Submit
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default EditDeadlineModal;
